import { useEffect } from "react";
import {
  createBrowserRouter,
  createRoutesFromElements,
  createSearchParams,
  Route,
  RouterProvider,
  useNavigate,
} from "react-router-dom";
import { QueryClientProvider } from "@tanstack/react-query";
import log from "loglevel";

//views
import HomeView from "@chemaxon/terminus-client/ui/views/home/HomeView";
import ListApplicationsView from "@chemaxon/terminus-client/ui/views/applications/list/ListApplicationsView";
import AddApplicationView from "@chemaxon/terminus-client/ui/views/applications/add/AddApplicationView";
import EditApplicationView from "@chemaxon/terminus-client/ui/views/applications/edit/EditApplicationView";
import ListTeamsView from "@chemaxon/terminus-client/ui/views/teams/list/ListTeamsView";
import AddTeamView from "@chemaxon/terminus-client/ui/views/teams/add/AddTeamView";
import EditTeamView from "@chemaxon/terminus-client/ui/views/teams/edit/EditTeamView";
import ListTeamMembersView from "@chemaxon/terminus-client/ui/views/team-members/list/ListTeamMembersView";
import AddTeamMembersView from "@chemaxon/terminus-client/ui/views/team-members/add/AddTeamMembersView";
import ListGroupsView from "@chemaxon/terminus-client/ui/views/groups/list/ListGroupsView";
import ListProjectsView from "@chemaxon/terminus-client/ui/views/projects/list/ListProjectsView";
import AddProjectView from "@chemaxon/terminus-client/ui/views/projects/add/AddProjectView";
import EditProjectView from "@chemaxon/terminus-client/ui/views/projects/edit/EditProjectView";
import ResourcesView from "@chemaxon/terminus-client/ui/views/resources/ResourcesView";
import { DefaultLayout } from "@chemaxon/terminus-client/ui/components/layout/Layout";
import SynergyTheme from "@chemaxon/terminus-client/ui/utils/SynergyTheme";
import {
  createTheme,
  StyledEngineProvider,
  ThemeProvider,
} from "@mui/material/styles";
import { CssBaseline } from "@mui/material";
import GlobalStyles from "@mui/material/GlobalStyles";
import { Background } from "@chemaxon/design-system/color";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { AppRoutePath } from "@chemaxon/terminus-client/ui/utils/routes";
import {
  useAuthentication,
  useGetMeQuery,
} from "@chemaxon/terminus-client/ui/services/UserInfoService";
import { useGetEnvQuery } from "@chemaxon/terminus-client/ui/services/EnvInfoService";
import { useGetPublicTenantInfoQuery } from "@chemaxon/terminus-client/ui/services/PublicTenantInfoService";
import { usePermissions } from "@chemaxon/terminus-client/ui/utils/usePermissions";
import { useFreshdesk } from "@chemaxon/terminus-client/ui/utils/useFreshdesk";
import { usePendo } from "@chemaxon/terminus-client/ui/utils/usePendo";
import {
  ErrorPage,
  errors as ErrorTypes,
  LoginErrorPage,
  RouterErrorPage,
} from "@chemaxon/terminus-client/ui/components/errorpage/ErrorPage";
import { useGetCsrfQuery } from "@chemaxon/terminus-client/ui/services/CsrfService";
import Api from "@chemaxon/terminus-client/ui/services/Api";
import queryClient from "@chemaxon/terminus-client/ui/utils/queryClient";
import { CustomAnalyticsProvider } from "@chemaxon/terminus-client/ui/utils/CustomAnalyticsProvider";
import Login from "@chemaxon/terminus-client/ui/components/login/Login";
import Eusa from "@chemaxon/terminus-client/ui/components/eusa/Eusa";
import { sessionStatus } from "@chemaxon/terminus-client/ui/services/sessionStatus";
import { SnackbarProvider } from "notistack";
import PropTypes from "prop-types";

const theme = createTheme(SynergyTheme);

const appGlobalStyles = (
  <GlobalStyles
    styles={{
      body: {
        backgroundColor: Background.variant,
        minHeight: "100vh",
      },
    }}
  />
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <CustomAnalyticsProvider>
      <StyledEngineProvider injectFirst>
        <ThemeProvider theme={theme}>
          <LocalizationProvider dateAdapter={AdapterMoment}>
            <AppInternal />
          </LocalizationProvider>
        </ThemeProvider>
      </StyledEngineProvider>
    </CustomAnalyticsProvider>
  </QueryClientProvider>
);

const Authentication = ({ children }) => {
  const getCsrfQueryResults = useGetCsrfQuery();
  const getEnvQueryResults = useGetEnvQuery();
  const auth = useAuthentication();
  const me = useGetMeQuery();

  useEffect(() => {
    if (getCsrfQueryResults.data) {
      Api.init(getCsrfQueryResults.data);
    }
  }, [getCsrfQueryResults.data]);

  useEffect(() => {
    if (getEnvQueryResults.data) {
      sessionStatus.setMaxInactiveInternal(
        getEnvQueryResults.data.maxInactiveInterval,
      );
      sessionStatus.createSessionTimeoutTimer();
    }
  }, [getEnvQueryResults.data]);

  if (auth.isPending) {
    return "Loading...";
  }

  if (!auth.data) {
    return <Login />;
  }

  if (!me?.data?.principal.user.acceptedEUSA) {
    return <Eusa />;
  }

  return <ExternalServices>{children}</ExternalServices>;
};

Authentication.propTypes = {
  children: PropTypes.element,
};

const ExternalServices = ({ children }) => {
  useFreshdesk();
  usePendo();

  const { queryResult: permissionsQueryResult } = usePermissions();
  const getMeQueryResults = useGetMeQuery();

  useEffect(() => {
    if (permissionsQueryResult.data && getMeQueryResults.data) {
      log.info("CURRENT USER: ", getMeQueryResults.data.principal.user);
      log.info("PERMISSIONS: ", permissionsQueryResult.data);
    }
  }, [permissionsQueryResult.data, getMeQueryResults.data]);

  if (
    permissionsQueryResult.data === undefined ||
    getMeQueryResults.data === undefined
  ) {
    return "Loading...";
  }
  return children;
};

const AppInternal = () => {
  const terminusRouter = createBrowserRouter(
    createRoutesFromElements(TerminusRoutes()),
  );

  return (
    <>
      <CssBaseline />
      {appGlobalStyles}
      <RouterProvider router={terminusRouter}></RouterProvider>
    </>
  );
};

const TenantCheck = ({ children }) => {
  const getPublicTenantInfoQueryResults = useGetPublicTenantInfoQuery();
  const navigate = useNavigate();

  if (getPublicTenantInfoQueryResults.isPending) {
    return "Loading...";
  }

  if (getPublicTenantInfoQueryResults?.data) {
    if (!getPublicTenantInfoQueryResults.data.exists) {
      navigate({
        pathname: AppRoutePath.ErrorPage,
        search: `?${createSearchParams({
          statusCode: ErrorTypes.TeamDoesNotExist.statusCode,
        })}`,
      });
    }
    if (getPublicTenantInfoQueryResults.data.expired) {
      navigate({
        pathname: AppRoutePath.ErrorPage,
        search: `?${createSearchParams({
          statusCode: ErrorTypes.TeamExpired.statusCode,
        })}`,
      });
    }
  }
  return children;
};

const TerminusRoutes = () => {
  return (
    <>
      <Route
        element={
          <TenantCheck>
            <Authentication>
              <SnackbarProvider>
                <DefaultLayout />
              </SnackbarProvider>
            </Authentication>
          </TenantCheck>
        }
      >
        <Route errorElement={<RouterErrorPage />}>
          <Route path={AppRoutePath.Dashboard} element={<HomeView />} />
          <Route
            path={AppRoutePath.ApplicationList}
            element={<ListApplicationsView />}
          />
          <Route
            path={AppRoutePath.ApplicationAdd}
            element={<AddApplicationView />}
          />
          <Route
            path={AppRoutePath.ApplicationEdit}
            element={<EditApplicationView />}
          />
          <Route path={AppRoutePath.TeamList} element={<ListTeamsView />} />
          <Route path={AppRoutePath.TeamAdd} element={<AddTeamView />} />
          <Route path={AppRoutePath.TeamEdit} element={<EditTeamView />} />
          <Route
            path={AppRoutePath.TeamMemberList}
            element={<ListTeamMembersView />}
          />
          <Route
            path={AppRoutePath.TeamMemberAdd}
            element={<AddTeamMembersView />}
          />
          <Route path={AppRoutePath.GroupList} element={<ListGroupsView />} />
          <Route
            path={AppRoutePath.ProjectList}
            element={<ListProjectsView />}
          />
          <Route path={AppRoutePath.ProjectAdd} element={<AddProjectView />} />
          <Route
            path={AppRoutePath.ProjectEdit}
            element={<EditProjectView />}
          />
          <Route path={AppRoutePath.ResourceList} element={<ResourcesView />} />
          <Route path="*" element={<ErrorPage isColored={false} />} />
        </Route>
      </Route>
      <Route path={AppRoutePath.ErrorPage} element={<ErrorPage />} />
      <Route path={AppRoutePath.LoginErrorPage} element={<LoginErrorPage />} />
    </>
  );
};

export default App;
